/**
 * @fileoverview Shopping Cart Custom Hooks
 *
 * This file contains all custom React hooks used by the ShoppingCart component
 * to manage different aspects of cart functionality and state.
 *
 * Core Hooks:
 * - useQuantityUpdate: Manages debounced quantity updates with API calls
 * - useLocalCartItems: Handles local cart state for immediate UI responsiveness
 * - useCartToast: Toast notification system with duplicate prevention
 * - useCheckoutManagement: Complete checkout workflow state management
 * - useOrderPlacement: Order submission logic with form validation
 * - useItemManagement: Cart item operations (add, update, remove)
 *
 * Key Features:
 * - Debounced API calls (1 second) to prevent rapid server requests
 * - Local state management for responsive UI updates
 * - Comprehensive error handling and user feedback
 * - Toast deduplication to prevent spam notifications
 * - Form validation integration for checkout process
 * - Automatic cleanup of timeouts and side effects
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { ShoppingCartItem, ToastState, CheckoutState } from "./ShoppingCart.types";
import { sortCartItemsByName, formatCheckoutPayload } from "./ShoppingCart.utils";
import {
  updateBasketItemQuantity,
  removeBasketItem,
  getCheckoutData,
  submitOrder,
} from "./ShoppingCart.api";
import { CheckoutFormRef } from "./CheckoutForm/CheckoutForm";

/**
 * Custom hook for managing quantity updates with debouncing
 * Prevents rapid API calls while providing responsive UI updates
 *
 * @param items - Current cart items
 * @param refreshCart - Function to refresh cart data after updates
 * @param onShowToast - Optional callback for displaying notifications
 * @returns Quantity update handler and loading state
 */
export const useQuantityUpdate = (
  items: ShoppingCartItem[],
  refreshCart: () => Promise<void>,
  onShowToast?: (title: string, text: string, success: boolean) => void
) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const debounceTimeouts = useRef<{ [key: number]: NodeJS.Timeout }>({});

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach((timeout) => {
        clearTimeout(timeout);
      });
    };
  }, []);

  const handleUpdateQuantity = useCallback(
    async (itemId: number, quantity: number) => {
      const item = items.find((cartItem) => cartItem.id === itemId);
      if (!item?.basketId) {
        onShowToast?.("Error", "Unable to update item quantity", false);
        return;
      }

      // Clear existing timeout for this item
      if (debounceTimeouts.current[itemId]) {
        clearTimeout(debounceTimeouts.current[itemId]);
      }

      // Set new timeout for API call
      debounceTimeouts.current[itemId] = setTimeout(async () => {
        setIsUpdating(true);

        let result;
        if (quantity === 0) {
          // Remove item if quantity is 0
          result = await removeBasketItem(item.basketId!);
        } else {
          // Update quantity if greater than 0
          result = await updateBasketItemQuantity(item.basketId!, itemId, quantity);
        }

        if (result.success) {
          await refreshCart();
          // Always show toast for successful updates - let useCartToast handle duplicates
          const message =
            quantity === 0
              ? result.message || "Item removed from basket"
              : result.message || "Item quantity updated";
          const title = quantity === 0 ? "Removed" : "Updated";
          onShowToast?.(title, message, true);
        } else {
          onShowToast?.("Error", result.message || "Failed to update item", false);
        }
        setIsUpdating(false);

        // Clean up timeout reference
        delete debounceTimeouts.current[itemId];
      }, 1000); // 1 second debounce
    },
    [items, refreshCart, onShowToast]
  );

  return {
    handleUpdateQuantity,
    isUpdating,
  };
};

/**
 * Custom hook for managing local cart items with immediate updates
 * Provides responsive UI by immediately updating local state while API calls process
 *
 * @param items - Server cart items that serve as the source of truth
 * @returns Local cart items and update functions
 */
export const useLocalCartItems = (items: ShoppingCartItem[]) => {
  const [localItems, setLocalItems] = useState<ShoppingCartItem[]>([]);

  // Sync local items with fetched items, maintaining alphabetical sort
  useEffect(() => {
    const sortedItems = sortCartItemsByName(items);
    setLocalItems(sortedItems);
  }, [items]);

  const updateLocalQuantity = useCallback((itemId: number, quantity: number) => {
    setLocalItems((prevItems) => {
      const updatedItems = prevItems.map((cartItem) =>
        cartItem.id === itemId ? { ...cartItem, quantity } : cartItem
      );
      // Maintain alphabetical sorting after quantity update
      return sortCartItemsByName(updatedItems);
    });
  }, []);

  const revertLocalItems = useCallback(() => {
    const sortedItems = sortCartItemsByName(items);
    setLocalItems(sortedItems);
  }, [items]);

  return {
    localItems,
    setLocalItems,
    updateLocalQuantity,
    revertLocalItems,
  };
};

// ========================================
// Toast Management Hook
// ========================================

/**
 * Custom hook for managing toast notifications in ShoppingCart
 * Prevents rapid duplicate toasts and manages proper cleanup
 * @param autoHideDuration - Duration in milliseconds before toast auto-hides
 * @returns Toast state and control functions
 */
export const useCartToast = (autoHideDuration: number = 5500) => {
  const [toast, setToast] = useState<ToastState | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastToastTimeRef = useRef<number>(0);
  const lastToastKeyRef = useRef<string>("");

  const showToast = (title: string, text: string, bSuccess: boolean = true) => {
    const now = Date.now();
    const toastKey = `${title}-${text}-${bSuccess}`;

    // Only prevent duplicates if it's the exact same toast within 2 seconds
    const timeSinceLastToast = now - lastToastTimeRef.current;
    const isSameToast = lastToastKeyRef.current === toastKey;
    const isWithinDuplicateWindow = timeSinceLastToast < 2000; // 2 seconds

    if (isSameToast && isWithinDuplicateWindow) {
      return; // Don't show duplicate toast
    }

    // Clear any existing timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    // Set new toast
    setToast({ show: true, title, text, bSuccess });
    lastToastTimeRef.current = now;
    lastToastKeyRef.current = toastKey;

    // Set new timeout to hide toast
    hideTimeoutRef.current = setTimeout(() => {
      setToast(null);
      hideTimeoutRef.current = null;
    }, autoHideDuration);
  };

  const hideToast = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setToast(null);
    lastToastKeyRef.current = "";
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  return {
    toast,
    showToast,
    hideToast,
  };
};

// ========================================
// Checkout Management Hook
// ========================================

/**
 * Custom hook for managing checkout workflow state and operations
 * Handles checkout mode switching, data fetching, and form state management
 *
 * @param refreshCart - Function to refresh cart data
 * @param updateItemsFromCheckoutData - Function to sync cart items with checkout data
 * @param showToast - Function to display toast notifications
 * @returns Checkout state and control functions
 */
export const useCheckoutManagement = (
  refreshCart: () => Promise<void>,
  updateItemsFromCheckoutData: (data: any) => void,
  showToast: (title: string, text: string, success: boolean) => void
) => {
  const [state, setState] = useState<CheckoutState>({
    mode: "cart",
    checkoutData: null,
    checkoutLoading: false,
    checkoutError: null,
    checkoutTotal: null,
    currentFormData: {},
  });

  /**
   * Resets checkout state to initial cart mode
   */
  const resetCheckoutState = useCallback(() => {
    setState((prev) => ({
      ...prev,
      mode: "cart",
      checkoutData: null,
      checkoutError: null,
      checkoutTotal: null,
      currentFormData: {},
    }));
  }, []);

  /**
   * Switches to checkout mode and fetches checkout data
   */
  const handleCheckout = useCallback(async () => {
    setState((prev) => ({ ...prev, checkoutLoading: true, checkoutError: null, mode: "checkout" }));

    try {
      const result = await getCheckoutData();
      if (result.success) {
        setState((prev) => ({
          ...prev,
          checkoutData: result.data,
          checkoutError: null,
          checkoutLoading: false,
        }));
        updateItemsFromCheckoutData(result.data);
      } else {
        console.error("Failed to load checkout data:", result.message);
        setState((prev) => ({
          ...prev,
          checkoutError: result.message || "Failed to load checkout data",
          checkoutLoading: false,
        }));
        showToast("Error", result.message || "Failed to load checkout data", false);
      }
    } catch (error) {
      console.error("Unexpected error during checkout:", error);
      setState((prev) => ({
        ...prev,
        checkoutError: "An unexpected error occurred",
        checkoutLoading: false,
      }));
      showToast("Error", "An unexpected error occurred", false);
    }
  }, [updateItemsFromCheckoutData, showToast]);

  /**
   * Cancels checkout and returns to cart mode
   */
  const handleCancelCheckout = useCallback(async () => {
    resetCheckoutState();
    await refreshCart();
  }, [resetCheckoutState, refreshCart]);

  /**
   * Updates checkout total from form calculations
   */
  const handleTotalUpdate = useCallback((newTotal: number) => {
    setState((prev) => ({ ...prev, checkoutTotal: newTotal }));
  }, []);

  /**
   * Updates form data state
   */
  const handleFormDataChange = useCallback((formData: Record<string, any>) => {
    setState((prev) => ({ ...prev, currentFormData: formData }));
  }, []);

  return {
    ...state,
    resetCheckoutState,
    handleCheckout,
    handleCancelCheckout,
    handleTotalUpdate,
    handleFormDataChange,
  };
};

// ========================================
// Order Placement Hook
// ========================================

/**
 * Custom hook for managing order placement workflow
 * Handles form validation, order submission, and post-order cleanup
 *
 * @param checkoutData - Current checkout data
 * @param currentFormData - Current form data
 * @param checkoutFormRef - Reference to checkout form for validation
 * @param showToast - Function to display toast notifications
 * @param onOrderComplete - Callback when order is successfully placed
 * @returns Order placement state and handler
 */
export const useOrderPlacement = (
  checkoutData: any,
  currentFormData: Record<string, any>,
  checkoutFormRef: React.RefObject<CheckoutFormRef>,
  showToast: (title: string, text: string, success: boolean) => void,
  onOrderComplete: () => void
) => {
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  /**
   * Validates form and places order
   */
  const handlePlaceOrder = useCallback(async () => {
    if (!checkoutData) {
      console.error("No checkout data available for order placement");
      showToast("Error", "Unable to place order - no checkout data available", false);
      return;
    }

    // Validate the checkout form before placing order
    if (checkoutFormRef.current) {
      const isFormValid = await checkoutFormRef.current.validateForm();
      if (!isFormValid) {
        showToast(
          "Address information missing",
          "Please ensure the address is complete before placing your order",
          false
        );
        return;
      }
    }

    setIsPlacingOrder(true);

    try {
      // Format the checkout payload using the current form data
      const payload = formatCheckoutPayload(currentFormData);

      // Submit the order
      const result = await submitOrder(payload);

      if (result.success) {
        // Clear persisted form data since order was placed successfully
        if (checkoutFormRef.current) {
          checkoutFormRef.current.clearPersistedData();
        }

        showToast(
          "Order Placed",
          result.message || "Your order has been placed successfully!",
          true
        );

        onOrderComplete();
      } else {
        console.error("Order submission failed:", result.message);
        showToast("Order Failed", result.message || "There was an error placing your order", false);
      }
    } catch (error) {
      console.error("Unexpected error during order placement:", error);
      showToast("Error", "An unexpected error occurred. Please try again.", false);
    } finally {
      setIsPlacingOrder(false);
    }
  }, [checkoutData, currentFormData, checkoutFormRef, showToast, onOrderComplete]);

  return {
    isPlacingOrder,
    handlePlaceOrder,
  };
};

// ========================================
// Item Management Hook
// ========================================

/**
 * Custom hook for managing cart item operations (quantity updates, removal)
 * Handles local state updates and API interactions for cart items
 *
 * @param items - Current cart items from server
 * @param localItems - Local cart items with immediate updates
 * @param updateLocalQuantity - Function to update local item quantities
 * @param refreshCart - Function to refresh cart data from server
 * @param showToast - Function to display toast notifications
 * @returns Item management functions and state
 */
export const useItemManagement = (
  items: ShoppingCartItem[],
  localItems: ShoppingCartItem[],
  updateLocalQuantity: (itemId: number, quantity: number) => void,
  refreshCart: () => Promise<void>,
  showToast: (title: string, text: string, success: boolean) => void
) => {
  const [localIsUpdating, setLocalIsUpdating] = useState(false);

  const { handleUpdateQuantity, isUpdating } = useQuantityUpdate(items, refreshCart, showToast);

  /**
   * Handles quantity changes with immediate local updates and debounced API calls
   */
  const handleQuantityChange = useCallback(
    async (itemId: number, quantity: number) => {
      // Update local state immediately for responsive UI
      updateLocalQuantity(itemId, quantity);
      // Call the debounced quantity update handler
      await handleUpdateQuantity(itemId, quantity);
    },
    [updateLocalQuantity, handleUpdateQuantity]
  );

  /**
   * Removes an item from the cart
   */
  const handleRemoveItem = useCallback(
    async (itemId: number) => {
      const item = items.find((cartItem) => cartItem.id === itemId);
      if (!item?.basketId) {
        showToast("Error", "Unable to remove item", false);
        return;
      }

      setLocalIsUpdating(true);
      const result = await removeBasketItem(item.basketId);

      if (result.success) {
        await refreshCart();
        showToast("Removed", result.message || "Item removed from basket", true);
      } else {
        showToast("Error", result.message || "Failed to remove item", false);
      }
      setLocalIsUpdating(false);
    },
    [items, refreshCart, showToast]
  );

  return {
    localIsUpdating,
    isUpdating,
    handleQuantityChange,
    handleRemoveItem,
  };
};
